/**
 * Which language has to be in memory before the first render (AL-1778).
 *
 * The app no longer bundles the four languages: each one is its own chunk,
 * fetched when it is needed. Only the active one is fetched at startup — the
 * fallbacks (`pt-BR` → `pt-PT` → `en`) are i18next's business, and it asks for them
 * if a key turns out to be missing.
 *
 * This is deliberately a pure function: it is the one piece of this change with
 * a decision in it, and the decision is which language "active" means when the
 * detector answers with something the app does not ship.
 */

export const DEFAULT_LANGUAGE = 'en';

/**
 * The language to preload for `detected`.
 *
 * Mirrors how i18next itself picks among `supportedLngs` (AL-1843): an exact
 * match first, ignoring case (`pt-br` → `pt-BR`); then the bare language if it
 * is supported (`es-AR` → `es`); then the first supported region of that
 * language (`pt` or `pt-AO` → `pt-PT`). Anything the app does not ship — or
 * nothing at all, before detection settles — resolves to the default, because
 * rendering with no language loaded is the one outcome that shows raw keys to
 * the user. Disagreeing with i18next here would fetch a chunk it never uses.
 */
export function languageToPreload(
  detected: string | undefined,
  supported: readonly string[]
): string {
  const wanted = detected?.trim().toLowerCase() ?? '';
  if (!wanted) return DEFAULT_LANGUAGE;

  const exact = supported.find(language => language.toLowerCase() === wanted);
  if (exact) return exact;

  const base = wanted.split('-')[0];
  const bare = supported.find(language => language.toLowerCase() === base);
  if (bare) return bare;

  const region = supported.find(language => language.toLowerCase().startsWith(`${base}-`));
  return region ?? DEFAULT_LANGUAGE;
}
